import { useState, useEffect, useCallback } from 'react';
import {
  useContract,
  useUpdateContract,
  useReleaseContract,
  useActivateMoraQuarantine,
  useReactivateMoraQuarantine,
  useCreateContractSubscriber,
  useUpdateContractSubscriber,
  useDeleteContractSubscriber,
  useCreateContractSubscriberArchived,
  useUpdateContractSubscriberArchived,
  useDeleteContractSubscriberArchived,
  useCreateContractAdditional,
  useUpdateContractAdditional,
  useDeleteContractAdditional,
} from '../../../hooks/useContracts';
import { useSubscribers } from '../../../hooks/useSubscribers';
import { useAdditionals } from '../../../hooks/useAdditionals';
import {
  DIALOG_TABS,
  MESSAGES,
  INITIAL_CONTRACT_STATE,
  INITIAL_SUBSCRIBER_STATE,
  INITIAL_ADDITIONAL_STATE
} from '../constants/contractsConstants';

const isActiveValue = (value) => value === true || value === 1 || value === '1' || value === 'true';

/**
 * Hook para manejar la lógica del diálogo de edición individual de contrato
 */
export const useContractIndividualEdit = ({
  contract,
  open,
  onSave,
  save,
  setSave,
  isArchived = false
}) => {
  // Estados principales
  const [localContract, setLocalContract] = useState(INITIAL_CONTRACT_STATE);
  const [dataModified, setDataModified] = useState(false);
  const [activeTab, setActiveTab] = useState(DIALOG_TABS.GENERAL_DATA);

  // Estados de suscriptores
  const [subscriberDialogOpen, setSubscriberDialogOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(INITIAL_SUBSCRIBER_STATE);
  const [editModeSubscriber, setEditModeSubscriber] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [matchingSubscribers, setMatchingSubscribers] = useState([]);

  // Estados de adicionales
  const [additionalDialogOpen, setAdditionalDialogOpen] = useState(false);
  const [selectedAdditional, setSelectedAdditional] = useState(INITIAL_ADDITIONAL_STATE);
  const [editModeAdditional, setEditModeAdditional] = useState(false);
  const [editDescription, setEditDescription] = useState(false);

  // Estados de notificaciones
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [modal, setModal] = useState({ open: false, message: '', onConfirm: null });
  const archivedSubscribersReadOnlyMessage =
    'No se pueden modificar suscriptores en contratos archivados.';

  // Datos de apoyo
  const { data: subscribers = [] } = useSubscribers();
  const { data: additionals = [] } = useAdditionals();
  
  const { data: freshContract } = useContract(contract?.uuid, {
    enabled: !!contract?.uuid && open,
  });

  // Mutations
  const updateContractMutation = useUpdateContract();
  const releaseContractMutation = useReleaseContract();
  const activateMoraQuarantineMutation = useActivateMoraQuarantine();
  const reactivateMoraQuarantineMutation = useReactivateMoraQuarantine();

  // Mutations para suscriptores - llamar todos los hooks (regla de React Hooks)
  const createSubscriberMutationRegular = useCreateContractSubscriber();
  const updateSubscriberMutationRegular = useUpdateContractSubscriber();
  const deleteSubscriberMutationRegular = useDeleteContractSubscriber();
  const createSubscriberMutationArchived = useCreateContractSubscriberArchived();
  const updateSubscriberMutationArchived = useUpdateContractSubscriberArchived();
  const deleteSubscriberMutationArchived = useDeleteContractSubscriberArchived();

  // Seleccionar qué mutations usar según si es archivado
  const createSubscriberMutation = isArchived ? createSubscriberMutationArchived : createSubscriberMutationRegular;
  const updateSubscriberMutation = isArchived ? updateSubscriberMutationArchived : updateSubscriberMutationRegular;
  const deleteSubscriberMutation = isArchived ? deleteSubscriberMutationArchived : deleteSubscriberMutationRegular;

  const createAdditionalMutation = useCreateContractAdditional();
  const updateAdditionalMutation = useUpdateContractAdditional();
  const deleteAdditionalMutation = useDeleteContractAdditional();

  // Inicializar estado local cuando cambia el contrato
  useEffect(() => {
    if (contract && open) {
      setLocalContract({ ...INITIAL_CONTRACT_STATE, ...contract });
      setDataModified(false);
      setActiveTab(DIALOG_TABS.GENERAL_DATA);
    }
  }, [contract, open]);

  useEffect(() => {
    if (freshContract && open) {
      setLocalContract(prev => ({
        ...prev,
        ...freshContract
      }));
    }
  }, [freshContract, open]);

  useEffect(() => {
    if (!open) {
      setLocalContract(INITIAL_CONTRACT_STATE);
      setSelectedSubscriber(INITIAL_SUBSCRIBER_STATE);
      setSelectedAdditional(INITIAL_ADDITIONAL_STATE);
      setSearchInput('');
      setMatchingSubscribers([]);
      setEditModeSubscriber(false);
      setEditModeAdditional(false);
      setEditDescription(false);
      setSubscriberDialogOpen(false);
      setAdditionalDialogOpen(false);
      setDataModified(false);
    }
  }, [open]);

  useEffect(() => {
    if (searchInput.length >= 3) {
      const matching = subscribers.filter(subscriber =>
        subscriber.dni?.includes(searchInput) ||
        subscriber.first_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
        subscriber.last_name?.toLowerCase().includes(searchInput.toLowerCase())
      );
      setMatchingSubscribers(matching);
    } else {
      setMatchingSubscribers([]);
    }
  }, [searchInput, subscribers]);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setLocalContract(prev => ({
      ...prev,
      [name]: value,
    }));
    setDataModified(true);
  }, []);

  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;
    setLocalContract(prev => ({
      ...prev,
      [name]: value === 'None' ? null : value,
    }));
    setDataModified(true);
  }, []);

  const handleSaveContract = useCallback(async () => {
    if (!dataModified) return;

    try {
      const directDebitCbu = String(localContract.direct_debit_cbu || '').replace(/\D/g, '');

      if (isActiveValue(localContract.direct_debit_enabled) && !/^\d{22}$/.test(directDebitCbu)) {
        setSnackbar({
          open: true,
          message: MESSAGES.INVALID_DIRECT_DEBIT_CBU,
          severity: 'error',
        });
        return;
      }

      await updateContractMutation.mutateAsync({
        ...localContract,
        direct_debit_cbu: directDebitCbu || null,
      });
      setDataModified(false);
      
      setSnackbar({
        open: true,
        message: MESSAGES.DATA_SAVED,
        severity: 'success',
      });

      if (onSave) onSave();
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [localContract, dataModified, updateContractMutation, onSave]);

  const handleOpenSubscriberDialog = useCallback(() => {
    if (isArchived) {
      setSnackbar({
        open: true,
        message: archivedSubscribersReadOnlyMessage,
        severity: 'warning',
      });
      return;
    }

    setSelectedSubscriber(INITIAL_SUBSCRIBER_STATE);
    setEditModeSubscriber(false);
    setSearchInput('');
    setSubscriberDialogOpen(true);
  }, [isArchived, archivedSubscribersReadOnlyMessage]);

  const handleCloseSubscriberDialog = useCallback(() => {
    setSubscriberDialogOpen(false);
    setSelectedSubscriber(INITIAL_SUBSCRIBER_STATE);
    setEditModeSubscriber(false);
    setSearchInput('');
    setMatchingSubscribers([]);
  }, []);

  const handleEditSubscriber = useCallback((subscriber) => {
    if (isArchived) {
      setSnackbar({
        open: true,
        message: archivedSubscribersReadOnlyMessage,
        severity: 'warning',
      });
      return;
    }

    setSelectedSubscriber(subscriber);
    setEditModeSubscriber(true);
    setSubscriberDialogOpen(true);
  }, [isArchived, archivedSubscribersReadOnlyMessage]);

  const handleSaveSubscriber = useCallback(async () => {
    if (isArchived) {
      setSnackbar({
        open: true,
        message: archivedSubscribersReadOnlyMessage,
        severity: 'warning',
      });
      return;
    }

    try {
      let result;

      if (editModeSubscriber) {
        result = await updateSubscriberMutation.mutateAsync({
          uuid_contract: localContract.uuid,
          uuid_subscriber: selectedSubscriber.subscriber.uuid,
          social_fee_payment: selectedSubscriber.social_fee_payment,
          life_insurance_percentage: selectedSubscriber.life_insurance_percentage,
          postage_address: selectedSubscriber.postage_address,
          mail_coupon: selectedSubscriber.mail_coupon,
        });

        setLocalContract(prev => ({
          ...prev,
          subscribers: prev.subscribers.map(sub =>
            sub.subscriber.uuid === selectedSubscriber.subscriber.uuid
              ? { ...sub, ...result }
              : sub
          )
        }));
      } else {
        if (!selectedSubscriber.subscriber) {
          setSnackbar({
            open: true,
            message: MESSAGES.SELECT_SUBSCRIBER,
            severity: 'error',
          });
          return;
        }

        result = await createSubscriberMutation.mutateAsync({
          uuid_contract: localContract.uuid,
          uuid_subscriber: selectedSubscriber.subscriber.uuid,
          social_fee_payment: selectedSubscriber.social_fee_payment,
          life_insurance_percentage: selectedSubscriber.life_insurance_percentage,
          postage_address: selectedSubscriber.postage_address,
          mail_coupon: selectedSubscriber.mail_coupon,
        });

        setLocalContract(prev => ({
          ...prev,
          subscribers: [...(prev.subscribers || []), result]
        }));
      }

      setSnackbar({
        open: true,
        message: MESSAGES.SUBSCRIBER_SAVED,
        severity: 'success',
      });

      handleCloseSubscriberDialog();
      if (setSave) setSave(!save);
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [
    isArchived,
    archivedSubscribersReadOnlyMessage,
    editModeSubscriber,
    selectedSubscriber,
    localContract,
    updateSubscriberMutation,
    createSubscriberMutation,
    save,
    setSave,
    handleCloseSubscriberDialog
  ]);

  const handleDeleteSubscriber = useCallback(async (subscriberUuid, contractUuid) => {
    if (isArchived) {
      setSnackbar({
        open: true,
        message: archivedSubscribersReadOnlyMessage,
        severity: 'warning',
      });
      return;
    }

    try {
      await deleteSubscriberMutation.mutateAsync({
        uuid_contract: contractUuid,
        uuid_subscriber: subscriberUuid
      });

      setLocalContract(prev => ({
        ...prev,
        subscribers: prev.subscribers.filter(
          sub => sub.subscriber.uuid !== subscriberUuid
        )
      }));

      setSnackbar({
        open: true,
        message: MESSAGES.SUBSCRIBER_DELETED,
        severity: 'success',
      });

      if (setSave) setSave(!save);
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [isArchived, archivedSubscribersReadOnlyMessage, deleteSubscriberMutation, save, setSave]);

  const handleInputChangeSubscriber = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedSubscriber(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubscriberSelection = useCallback((subscriber) => {
    setSelectedSubscriber(prev => ({
      ...prev,
      subscriber: subscriber,
    }));
    setSearchInput(subscriber ? subscriber.dni : '');
  }, []);

  const handleInputChangeDNISubscriber = useCallback((event) => {
    const value = event.target.value;
    setSearchInput(value);
  }, []);

  const handleOpenAdditionalDialog = useCallback(() => {
    setSelectedAdditional(INITIAL_ADDITIONAL_STATE);
    setEditModeAdditional(false);
    setEditDescription(false);
    setAdditionalDialogOpen(true);
  }, []);

  const handleCloseAdditionalDialog = useCallback(() => {
    setAdditionalDialogOpen(false);
    setSelectedAdditional(INITIAL_ADDITIONAL_STATE);
    setEditModeAdditional(false);
    setEditDescription(false);
  }, []);

  const handleEditAdditional = useCallback((additional) => {
    setSelectedAdditional(additional);
    setEditModeAdditional(true);
    setEditDescription(true);
    setAdditionalDialogOpen(true);
  }, []);

  const handleSaveAdditional = useCallback(async () => {
    try {
      if (editModeAdditional) {
        await updateAdditionalMutation.mutateAsync({
          uuid: selectedAdditional.uuid,
          description: selectedAdditional.description,
          amount: selectedAdditional.amount,
        });
      } else {
        if (!selectedAdditional.additional) {
          setSnackbar({
            open: true,
            message: MESSAGES.SELECT_ADDITIONAL,
            severity: 'error',
          });
          return;
        }

        await createAdditionalMutation.mutateAsync({
          uuid_contract: localContract.uuid,
          uuid_additional: selectedAdditional.additional.uuid,
          description: selectedAdditional.description,
          amount: selectedAdditional.amount,
          current_fee: localContract.last_generated_fee || 1,
        });
      }

      setSnackbar({
        open: true,
        message: MESSAGES.ADDITIONAL_SAVED,
        severity: 'success',
      });

      handleCloseAdditionalDialog();
      if (setSave) setSave(!save);
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [
    editModeAdditional,
    selectedAdditional,
    localContract,
    updateAdditionalMutation,
    createAdditionalMutation,
    save,
    setSave,
    handleCloseAdditionalDialog
  ]);

  const handleDeleteAdditional = useCallback(async (additionalUuid) => {
    try {
      await deleteAdditionalMutation.mutateAsync({
        uuid: additionalUuid,
        uuid_contract: localContract.uuid
      });

      setSnackbar({
        open: true,
        message: MESSAGES.ADDITIONAL_DELETED,
        severity: 'success',
      });

      if (setSave) setSave(!save);
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [deleteAdditionalMutation, localContract.uuid, save, setSave]);

  const handleInputChangeAdditional = useCallback((event) => {
    const { name, value } = event.target;
    
    if (name === 'uuid') {
      const selectedAdd = additionals.find(add => add.uuid === value);
      setSelectedAdditional(prev => ({
        ...prev,
        additional: selectedAdd,
      }));
    } else {
      setSelectedAdditional(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  }, [additionals]);

  const handleReleaseContract = useCallback(async () => {
    if (!localContract.uuid_release_reason || !localContract.release_reason_obs) {
      setSnackbar({
        open: true,
        message: MESSAGES.COMPLETE_RELEASE_DATA,
        severity: 'error',
      });
      return;
    }

    try {
      const releasedContract = await releaseContractMutation.mutateAsync({
        uuid: localContract.uuid,
        release_reason_obs: localContract.release_reason_obs,
        uuid_release_reason: localContract.uuid_release_reason,
      });

      const successMessage = releasedContract?.is_archived
        ? MESSAGES.CONTRACT_ARCHIVED
        : MESSAGES.CONTRACT_RELEASED;

      setModal({
        open: true,
        message: successMessage,
        onConfirm: () => {
          if (setSave) setSave(!save);
          if (onSave) onSave();
        },
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [localContract, releaseContractMutation, save, setSave, onSave]);

  const handleActivateMoraQuarantine = useCallback(async () => {
    try {
      if (dataModified) {
        await updateContractMutation.mutateAsync(localContract);
        setDataModified(false);
      }

      const updatedContract = await activateMoraQuarantineMutation.mutateAsync({
        uuid: localContract.uuid,
      });

      setLocalContract((prev) => ({ ...prev, ...updatedContract }));
      setModal({
        open: true,
        message: MESSAGES.CONTRACT_MORA_ACTIVATED,
        onConfirm: () => {
          if (setSave) setSave(!save);
          if (onSave) onSave();
        },
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [
    dataModified,
    updateContractMutation,
    localContract,
    activateMoraQuarantineMutation,
    save,
    setSave,
    onSave,
  ]);

  const handleReactivateMoraQuarantine = useCallback(async () => {
    try {
      if (dataModified) {
        await updateContractMutation.mutateAsync(localContract);
        setDataModified(false);
      }

      const updatedContract = await reactivateMoraQuarantineMutation.mutateAsync({
        uuid: localContract.uuid,
      });

      setLocalContract((prev) => ({ ...prev, ...updatedContract }));
      setModal({
        open: true,
        message: MESSAGES.CONTRACT_MORA_REACTIVATED,
        onConfirm: () => {
          if (setSave) setSave(!save);
          if (onSave) onSave();
        },
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: MESSAGES.ERROR_OCCURRED,
        severity: 'error',
      });
    }
  }, [
    dataModified,
    updateContractMutation,
    localContract,
    reactivateMoraQuarantineMutation,
    save,
    setSave,
    onSave,
  ]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setModal(prev => {
      if (prev.onConfirm) {
        prev.onConfirm();
      }
      return { open: false, message: '', onConfirm: null };
    });
  }, []);

  return {
    // Estado del contrato
    localContract,
    dataModified,
    activeTab,
    setActiveTab,

    // Estados de suscriptores
    subscriberDialogOpen,
    selectedSubscriber,
    editModeSubscriber,
    searchInput,
    matchingSubscribers,

    // Estados de adicionales
    additionalDialogOpen,
    selectedAdditional,
    editModeAdditional,
    editDescription,

    // Estados de notificaciones
    snackbar,
    modal,

    // Datos de apoyo
    additionals,

    // Funciones principales
    handleInputChange,
    handleSelectChange,
    handleSaveContract,

    // Funciones de suscriptores
    handleOpenSubscriberDialog,
    handleCloseSubscriberDialog,
    handleEditSubscriber,
    handleSaveSubscriber,
    handleDeleteSubscriber,
    handleInputChangeSubscriber,
    handleSubscriberSelection,
    handleInputChangeDNISubscriber,

    // Funciones de adicionales
    handleOpenAdditionalDialog,
    handleCloseAdditionalDialog,
    handleEditAdditional,
    handleSaveAdditional,
    handleDeleteAdditional,
    handleInputChangeAdditional,

    // Función de liberación
    handleReleaseContract,
    handleActivateMoraQuarantine,
    handleReactivateMoraQuarantine,
    isMoraActionLoading:
      activateMoraQuarantineMutation.isPending || reactivateMoraQuarantineMutation.isPending,

    // Funciones de notificaciones
    handleCloseSnackbar,
    handleCloseModal,
  };
};
